/**
 * The themed card shows one top-level section of the survey at a time. These helpers turn the
 * survey's own level (already in render order - see `orderedItems`) into steps, and work out which
 * step is shown and how far along the respondent is.
 *
 * Every step stays mounted while hidden, because submitting, saving a draft and printing all read
 * the rendered questions straight from the DOM.
 */

/**
 * @param {Array} items  The survey's top-level items, in render order.
 * @param {{ introSectionName?: string }} options
 * @returns {Array<{key: string, section: object|null, leadingItems: Array, trailingItems: Array,
 *    isIntro: boolean, number?: number}>}
 */
export const buildSteps = (items = [], { introSectionName } = {}) => {
   const steps = [];
   let pending = [];

   items.forEach((item) => {
      if (item.itemLabel === "Section") {
         steps.push({
            key: item.id,
            section: item,
            // A question that sits outside every section is shown with the section after it.
            leadingItems: pending,
            trailingItems: [],
            isIntro: Boolean(introSectionName) && item.surveyItem?.name === introSectionName
         });
         pending = [];
      } else {
         pending.push(item);
      }
   });

   if (pending.length > 0) {
      if (steps.length > 0) {
         steps[steps.length - 1].trailingItems = pending;
      } else {
         steps.push({
            key: "survey-root",
            section: null,
            leadingItems: pending,
            trailingItems: [],
            isIntro: false
         });
      }
   }

   // Sections keep their number whether or not skip logic hides another one, so "06 Për burrat"
   // is 06 for everyone who sees it.
   let number = 0;
   return steps.map((step) => (step.isIntro ? step : { ...step, number: ++number }));
};

export const getSectionTitle = (section) =>
   section?.sectionLabel?.description ?? section?.surveyItem?.description ?? "";

export const formatStepNumber = (number) => String(number ?? "").padStart(2, "0");

export const getVisibleSteps = (steps, hiddenByKey = {}) =>
   steps.filter((step) => !hiddenByKey[step.key]);

/**
 * The step to show. When skip logic hides the step that was showing, fall back to the closest
 * visible step before it, so the respondent never jumps ahead past unanswered questions.
 */
export const resolveActiveKey = (steps, visibleSteps, activeKey) => {
   if (visibleSteps.length === 0) {
      return null;
   }
   if (visibleSteps.some((step) => step.key === activeKey)) {
      return activeKey;
   }

   const activeIndex = steps.findIndex((step) => step.key === activeKey);
   for (let i = activeIndex - 1; i >= 0; i--) {
      if (visibleSteps.includes(steps[i])) {
         return steps[i].key;
      }
   }

   return visibleSteps[0].key;
};

/** Position among the visible content steps - the intro does not count. */
export const getProgress = (visibleSteps, activeKey) => {
   const contentSteps = visibleSteps.filter((step) => !step.isIntro);
   const index = contentSteps.findIndex((step) => step.key === activeKey);

   return {
      position: index + 1,
      total: contentSteps.length
   };
};
